import React, { Suspense, lazy } from 'react';
import { Hero } from './components/Hero';

/**
 * CATIO READY™ sales page.
 * Pre-offer:  HERO → VALUE → REAL PAGES (technical first) → BUILDER SERVICE + RIGHTS →
 *             PLAN → SYSTEM + TECH PROOF → 5 VOLUMES → 15 PROJECTS → FREE BONUSES →
 *             VALUE STACK → OFFER
 * Post-offer: VOLUME DETAIL → OBJECTIONS → ACCESS → FAQ → INFO → FINAL CTA
 *
 * Everything below the hero loads in separate chunks so the hero (mobile LCP)
 * ships with minimal JS. Each block reserves height while loading.
 */
const named = <K extends string,>(loader: () => Promise<Record<K, React.ComponentType>>, key: K) => lazy(() => loader().then(m => ({
  default: m[key]
})));
const ValueStrip = named(() => import('./components/sections/ValueStrip'), 'ValueStrip');
const PageProofSection = named(() => import('./components/sections/PageProofSection'), 'PageProofSection');
const PlanSection = named(() => import('./components/sections/PlanSection'), 'PlanSection');
const SystemSection = named(() => import('./components/sections/SystemSection'), 'SystemSection');
const CollectionSection = named(() => import('./components/sections/CollectionSection'), 'CollectionSection');
const GallerySection = named(() => import('./components/sections/GallerySection'), 'GallerySection');
const ServiceSection = named(() => import('./components/sections/ServiceSection'), 'ServiceSection');
const BonusSection = named(() => import('./components/sections/BonusSection'), 'BonusSection');
const ValueStackSection = named(() => import('./components/sections/ValueStackSection'), 'ValueStackSection');
const OfferSection = named(() => import('./components/sections/OfferSection'), 'OfferSection');
const VolumeDetailSection = named(() => import('./components/sections/VolumeDetailSection'), 'VolumeDetailSection');
const ObjectionSection = named(() => import('./components/sections/ObjectionSection'), 'ObjectionSection');
const AccessSection = named(() => import('./components/sections/AccessSection'), 'AccessSection');
const FaqSection = named(() => import('./components/sections/FaqSection'), 'FaqSection');
const DisclaimerSection = named(() => import('./components/sections/DisclaimerSection'), 'DisclaimerSection');
const FinalCta = named(() => import('./components/sections/FinalCta'), 'FinalCta');
const Footer = named(() => import('./components/Footer'), 'Footer');
const StickyBuyBar = named(() => import('./components/StickyBuyBar'), 'StickyBuyBar');
interface DeferredProps {
  children: React.ReactNode;
  minHeight: string;
  background?: string;
  id?: string;
}

/** The fallback carries the section id so #offer resolves even before the chunk loads. */
function Deferred({
  children,
  minHeight,
  background = 'bg-ivory',
  id
}: DeferredProps) {
  return <Suspense fallback={<div id={id} className={`w-full ${background}`} style={{
    minHeight
  }} aria-hidden data-id="element-1" data-mp="2xun2dmuexn3f" />} data-id="element-0" data-mp="3liefgsns245r">
      {children}
    </Suspense>;
}
export function App() {
  return <main className="w-full overflow-x-clip bg-ivory text-ink" data-id="element-2" data-mp="2stpklbdbev2g">
      <Hero data-id="element-3" data-mp="18czm8goc19lf" />

      <Deferred minHeight="96px" data-id="element-4" data-mp="18c6qeaqww4i2">
        <ValueStrip data-id="element-5" data-mp="1d4hmt3mg185v" />
      </Deferred>
      <Deferred minHeight="820px" background="bg-paper" data-id="element-6" data-mp="1d5jbiawdtt62">
        <PageProofSection data-id="element-7" data-mp="yfli9gjlida0" />
      </Deferred>
      <Deferred minHeight="760px" background="bg-stone" data-id="element-8" data-mp="ygmsxjwp8xr7">
        <ServiceSection data-id="element-9" data-mp="13eodyador75b" />
      </Deferred>
      <Deferred minHeight="760px" data-id="element-10" data-mp="13fpzj8sbvc1a">
        <PlanSection data-id="element-11" data-mp="13clq9x009q3r" />
      </Deferred>
      <Deferred minHeight="1400px" background="bg-paper" data-id="element-12" data-mp="1r3r5c9y4ged0">
        <SystemSection data-id="element-13" data-mp="1r267aaclmdae" />
      </Deferred>
      <Deferred minHeight="820px" background="bg-stone" data-id="element-14" data-mp="1w3vlfyv4b72p">
        <CollectionSection data-id="element-15" data-mp="1w19dhy4bepjy" />
      </Deferred>
      <Deferred minHeight="900px" background="bg-ink" data-id="element-16" data-mp="1i4ce38a4rzrg">
        <GallerySection data-id="element-17" data-mp="1i5nkzgyvhnms" />
      </Deferred>
      <Deferred minHeight="1200px" background="bg-paper" data-id="element-18" data-mp="1m4eihp7937af">
        <BonusSection data-id="element-19" data-mp="1m70p9mmbhirl" />
      </Deferred>
      <Deferred minHeight="900px" background="bg-stone" data-id="element-20" data-mp="6o5xls1ln0mf">
        <ValueStackSection data-id="element-21" data-mp="6qs4rqkkve44" />
      </Deferred>

      <Deferred minHeight="1100px" background="bg-ink" id="offer" data-id="element-22" data-mp="aq98ofkcw376">
        <OfferSection data-id="element-23" data-mp="qx5gpzii0qb0" />
      </Deferred>

      <Deferred minHeight="560px" background="bg-paper" data-id="element-24" data-mp="qy714ulemp5e">
        <VolumeDetailSection data-id="element-25" data-mp="m6f8meiou9cm" />
      </Deferred>
      <Deferred minHeight="760px" data-id="element-26" data-mp="m5nhbfw4ixs5">
        <ObjectionSection data-id="element-27" data-mp="m4m3j3nq3x6q" />
      </Deferred>
      <Deferred minHeight="420px" background="bg-stone" data-id="element-28" data-mp="h6khqaywxjrk">
        <AccessSection data-id="element-29" data-mp="h4znynkfa4ur" />
      </Deferred>
      <Deferred minHeight="760px" data-id="element-30" data-mp="c90y0vjg4es8">
        <FaqSection data-id="element-31" data-mp="ca2itrq9kfn5" />
      </Deferred>
      <Deferred minHeight="280px" data-id="element-32" data-mp="19vrh1c66yddv">
        <DisclaimerSection data-id="element-33" data-mp="19uot9d460r26" />
      </Deferred>
      <Deferred minHeight="620px" background="bg-paper" data-id="element-34" data-mp="19tndivc7fge4">
        <FinalCta data-id="element-35" data-mp="14y83yfl8c6ni" />
      </Deferred>
      <Deferred minHeight="260px" background="bg-ink" data-id="element-36" data-mp="14v3up3swqkpz">
        <Footer data-id="element-37" data-mp="zwis4xq6noyd" />
      </Deferred>

      <Suspense fallback={null} data-id="element-38" data-mp="zzwu44uxpw5a">
        <StickyBuyBar data-id="element-39" data-mp="vwrss5mlc0i8" />
      </Suspense>
    </main>;
}